'use client';

import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import Link from 'next/link';
import { useQuery } from '@tanstack/react-query';
import * as Select from '@radix-ui/react-select';
import { ArrowRight, CaretDown, X } from '@phosphor-icons/react';
import { CheckIcon, MapPinIcon, SearchIcon, SpinnerIcon } from '@/components/icons';
import {
  deliveryApi,
  type AddressSuggestion,
  type City,
  type DeliveryCheckResult,
} from '@/lib/api/delivery';
import { useAddressStore } from '@/stores/address-store';
import { useMoneyRules } from '@/lib/money-rules';
import { deliveryMoney, deliveryTimeLabel } from './delivery-model';
import './delivery-address-controls.css';

export interface CheckedMapAddress {
  lat: number;
  lon: number;
  label: string;
}

interface Props {
  cities: City[];
  city?: City;
  citiesLoading: boolean;
  citiesError: boolean;
  onCityChange: (city: City) => void;
  onRetryCities: () => void;
  onChecked: (zoneId?: number, marker?: CheckedMapAddress) => void;
}

function suggestionLines(suggestion: AddressSuggestion, city?: City) {
  const parts = suggestion.value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  const cityName = suggestion.data.city || city?.name;
  const localParts = parts.filter((part) => {
    const plain = part.replace(/^г\.?\s+/i, '').trim();
    return (
      plain !== cityName && plain !== suggestion.data.region && plain !== suggestion.data.country
    );
  });
  return {
    street: localParts.join(', ') || suggestion.value,
    city: [cityName, suggestion.data.region !== cityName ? suggestion.data.region : null]
      .filter(Boolean)
      .join(' · '),
  };
}

export function DeliveryAddressForm({
  cities,
  city,
  citiesLoading,
  citiesError,
  onCityChange,
  onRetryCities,
  onChecked,
}: Props) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const requestRef = useRef(0);
  const busyRef = useRef(false);
  const fieldRef = useRef<HTMLDivElement>(null);
  // Палец или мышь на подсказке: поле может потерять фокус раньше, чем придёт click.
  const interactingRef = useRef(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState(-1);
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<DeliveryCheckResult | null>(null);
  const [error, setError] = useState('');
  const [checkedAddress, setCheckedAddress] = useState('');
  const savedAddress = useAddressStore((state) => state.selectedAddress);
  const hydrated = useAddressStore((state) => state._hasHydrated);
  const checkAddress = useAddressStore((state) => state.checkAddress);
  const freeDelivery = useMoneyRules().free_delivery;

  useEffect(
    () => () => {
      ++requestRef.current;
      busyRef.current = false;
    },
    []
  );

  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(query.trim()), 280);
    return () => clearTimeout(timeout);
  }, [query]);

  const suggestionsQuery = useQuery({
    queryKey: ['delivery-page-suggestions', city?.id, debounced],
    queryFn: () => deliveryApi.autocomplete(debounced, city?.name),
    enabled: !!city && focused && debounced.length >= 3 && !pending,
    staleTime: 30000,
    retry: false,
  });
  // Улица, которую человек только что выбрал, в списке уже не нужна — остаются её дома.
  // Иначе она снова стояла первой, и выбор выглядел несработавшим.
  const suggestions =
    focused && query.trim() === debounced
      ? (suggestionsQuery.data?.suggestions ?? []).filter(
          (suggestion) => suggestion.data.house || suggestion.value !== debounced
        )
      : [];
  const searching = focused && !!city && !pending && query.trim().length >= 3;
  const suggestionsLoading =
    searching && (query.trim() !== debounced || suggestionsQuery.isFetching);
  const showSuggestions = searching && suggestions.length > 0;
  // «Не нашли» — только когда подсказка действительно ничего не вернула, а не когда
  // в ответе была одна лишь выбранная улица.
  const suggestionsEmpty =
    searching &&
    !suggestionsLoading &&
    suggestionsQuery.isSuccess &&
    !suggestionsQuery.data?.suggestions?.length;

  useEffect(() => {
    if (active >= 0)
      document.getElementById(`${id}-suggestion-${active}`)?.scrollIntoView({ block: 'nearest' });
  }, [active, id]);

  // Список закрывается нажатием вне поля, а не только потерей фокуса: после тапа по
  // подсказке фокуса в поле уже может не быть, и событие blur больше не придёт.
  useEffect(() => {
    if (!focused) return;
    const closeOutside = (event: PointerEvent) => {
      if (fieldRef.current?.contains(event.target as Node | null)) return;
      interactingRef.current = false;
      setFocused(false);
      setActive(-1);
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, [focused]);

  const updateQuery = (value: string) => {
    setQuery(value);
    setResult(null);
    setError('');
    setActive(-1);
    onChecked();
  };

  const check = async (address: string, coordinates?: { lat: number; lon: number }) => {
    if (!city || !address.trim() || busyRef.current) return;
    const request = ++requestRef.current;
    busyRef.current = true;
    setFocused(false);
    setPending(true);
    setResult(null);
    setError('');
    onChecked();
    try {
      const nextResult = await checkAddress(address.trim(), coordinates);
      if (request !== requestRef.current) return;
      if (nextResult.error) {
        setError('Не удалось проверить адрес. Попробуйте ещё раз или позвоните нам.');
        return;
      }
      setCheckedAddress(address.trim());
      setResult(nextResult);
      if (nextResult.isInZone && coordinates) {
        const state = useAddressStore.getState();
        if (state.selectedAddress?.address === address.trim()) {
          state.setSelectedAddress({ ...state.selectedAddress, coordinates });
        }
      }
      onChecked(
        nextResult.zone?.id,
        coordinates ? { ...coordinates, label: address.trim() } : undefined
      );
    } catch {
      if (request === requestRef.current) {
        setError('Не удалось проверить адрес. Попробуйте ещё раз или позвоните нам.');
      }
    } finally {
      if (request === requestRef.current) {
        busyRef.current = false;
        setPending(false);
      }
    }
  };

  const choose = (suggestion: AddressSuggestion) => {
    interactingRef.current = false;
    // Сначала снимаем фокус: клавиатура телефона завершает незаконченное слово
    // (IME composition) ДО подстановки адреса. Пока фокус оставался в поле, часть
    // клавиатур возвращала набранный текст поверх выбранной подсказки.
    inputRef.current?.blur();
    setResult(null);
    setError('');
    onChecked();
    setFocused(false);
    setActive(-1);
    if (!suggestion.data.house) {
      // Осталось дописать дом: возвращаем клавиатуру в том же жесте (иначе iOS её не покажет)
      setQuery(`${suggestion.value} `);
      setError('Добавьте номер дома, чтобы точно проверить доставку.');
      inputRef.current?.focus();
      return;
    }
    setQuery(suggestion.value);
    const lat = Number(suggestion.data.geo_lat);
    const lon = Number(suggestion.data.geo_lon);
    const coordinates =
      suggestion.data.geo_lat &&
      suggestion.data.geo_lon &&
      Number.isFinite(lat) &&
      Number.isFinite(lon)
        ? { lat, lon }
        : undefined;
    void check(suggestion.value, coordinates);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (showSuggestions && active >= 0 && suggestions[active]) choose(suggestions[active]);
    else void check(query);
  };

  return (
    <div className="delivery-address-panel">
      <h2>Куда привезти?</h2>
      <p className="delivery-panel-description">Узнайте стоимость и время доставки.</p>
      <form onSubmit={submit} className="delivery-address-form">
        <div className="delivery-form-field">
          <label id={`${id}-city-label`} htmlFor={cities.length > 1 ? `${id}-city` : undefined}>
            Город доставки
          </label>
          {cities.length > 1 ? (
            <Select.Root
              value={city ? String(city.id) : undefined}
              disabled={citiesLoading || pending}
              onOpenChange={(open) => {
                if (open) setFocused(false);
              }}
              onValueChange={(value) => {
                const next = cities.find((item) => String(item.id) === value);
                if (!next || next.id === city?.id) return;
                ++requestRef.current;
                updateQuery('');
                setFocused(false);
                onCityChange(next);
              }}
            >
              <Select.Trigger
                id={`${id}-city`}
                className="delivery-city-trigger"
                aria-labelledby={`${id}-city-label ${id}-city-value`}
              >
                <span className="delivery-control-icon">
                  <MapPinIcon size={19} aria-hidden="true" />
                </span>
                <Select.Value id={`${id}-city-value`} placeholder="Выберите город" />
                <Select.Icon className="delivery-city-chevron">
                  <CaretDown size={16} aria-hidden="true" />
                </Select.Icon>
              </Select.Trigger>
              <Select.Portal>
                <Select.Content
                  className="delivery-city-menu"
                  position="popper"
                  sideOffset={8}
                  collisionPadding={16}
                >
                  <Select.Viewport className="delivery-city-viewport">
                    <Select.Group>
                      <Select.Label className="delivery-city-menu-label">
                        Выберите город
                      </Select.Label>
                      {cities.map((item) => (
                        <Select.Item
                          key={item.id}
                          value={String(item.id)}
                          textValue={item.name}
                          className="delivery-city-option"
                        >
                          <MapPinIcon size={18} aria-hidden="true" />
                          <Select.ItemText>{item.name}</Select.ItemText>
                          <Select.ItemIndicator className="delivery-city-selected">
                            <CheckIcon size={16} weight="bold" />
                          </Select.ItemIndicator>
                        </Select.Item>
                      ))}
                    </Select.Group>
                  </Select.Viewport>
                </Select.Content>
              </Select.Portal>
            </Select.Root>
          ) : (
            <div
              className="delivery-city-fixed"
              role="group"
              aria-labelledby={`${id}-city-label`}
              aria-busy={citiesLoading}
            >
              <span className="delivery-control-icon">
                <MapPinIcon size={19} aria-hidden="true" />
              </span>
              <span>
                {city?.name || (citiesLoading ? 'Загружаем города…' : 'Город недоступен')}
              </span>
              {citiesLoading ? (
                <SpinnerIcon size={18} className="delivery-spinner" aria-hidden="true" />
              ) : city ? (
                <CheckIcon size={16} className="delivery-city-check" aria-hidden="true" />
              ) : null}
            </div>
          )}
        </div>
        <div
          ref={fieldRef}
          className="delivery-form-field delivery-street-field"
          onBlur={(event) => {
            // Идёт тап по подсказке: список обязан дожить до click, иначе выбор теряется.
            if (interactingRef.current) return;
            if (!event.currentTarget.contains(event.relatedTarget)) {
              setFocused(false);
              setActive(-1);
            }
          }}
        >
          <label htmlFor={`${id}-address`}>Улица и номер дома</label>
          <div className="delivery-address-control-group">
            <div
              className={`delivery-input-wrap delivery-address-control${error ? ' has-error' : ''}${pending ? ' is-checking' : ''}`}
            >
              <span className="delivery-control-icon">
                <SearchIcon size={20} aria-hidden="true" />
              </span>
              <input
                id={`${id}-address`}
                ref={inputRef}
                type="text"
                autoComplete="off"
                enterKeyHint="go"
                spellCheck={false}
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={showSuggestions}
                aria-controls={showSuggestions ? `${id}-suggestions` : undefined}
                aria-activedescendant={
                  showSuggestions && active >= 0 && suggestions[active]
                    ? `${id}-suggestion-${active}`
                    : undefined
                }
                aria-describedby={error ? `${id}-error` : `${id}-address-hint`}
                aria-invalid={!!error}
                aria-busy={pending}
                placeholder="Улица, дом"
                value={query}
                disabled={pending || !city}
                onFocus={() => {
                  interactingRef.current = false;
                  setFocused(true);
                }}
                onChange={(event) => {
                  updateQuery(event.target.value);
                  setFocused(true);
                }}
                onKeyDown={(event) => {
                  if (event.nativeEvent.isComposing) return;
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    setFocused(false);
                    setActive(-1);
                  }
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    if (showSuggestions && active >= 0 && suggestions[active])
                      choose(suggestions[active]);
                    else void check(query);
                  }
                  if (showSuggestions && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
                    event.preventDefault();
                    setActive((current) =>
                      event.key === 'ArrowDown'
                        ? Math.min(suggestions.length - 1, current + 1)
                        : current < 0
                          ? suggestions.length - 1
                          : Math.max(0, current - 1)
                    );
                  }
                }}
              />
              {pending || suggestionsLoading ? (
                <span className="delivery-input-progress" aria-hidden="true">
                  <SpinnerIcon size={20} className="delivery-spinner" />
                </span>
              ) : query ? (
                <button
                  type="button"
                  className="delivery-clear"
                  aria-label="Очистить адрес"
                  onClick={() => {
                    updateQuery('');
                    inputRef.current?.focus();
                  }}
                >
                  <X size={17} aria-hidden="true" />
                </button>
              ) : null}
            </div>
            {showSuggestions ? (
              <div className="delivery-suggestion-popover">
                <div className="delivery-suggestions-heading" aria-hidden="true">
                  Выберите адрес
                </div>
                <ul
                  className="delivery-suggestions"
                  id={`${id}-suggestions`}
                  role="listbox"
                  aria-label="Подсказки адреса"
                >
                  {suggestions.map((suggestion, index) => {
                    const lines = suggestionLines(suggestion, city);
                    return (
                      <li
                        key={`${suggestion.value}-${index}`}
                        role="option"
                        id={`${id}-suggestion-${index}`}
                        aria-selected={active === index}
                        // Без preventDefault: поле теряет фокус естественно, как у обычной
                        // кнопки, а список держит флаг interactingRef (см. address-input.tsx).
                        onPointerDown={() => {
                          interactingRef.current = true;
                        }}
                        onPointerCancel={() => {
                          interactingRef.current = false;
                        }}
                        onClick={() => choose(suggestion)}
                      >
                        <span className="delivery-suggestion-icon">
                          <MapPinIcon size={18} aria-hidden="true" />
                        </span>
                        <span className="delivery-suggestion-copy">
                          <strong>{lines.street}</strong>
                          {lines.city && <small>{lines.city}</small>}
                          {!suggestion.data.house && (
                            <small className="delivery-suggestion-incomplete">
                              Уточните номер дома
                            </small>
                          )}
                        </span>
                        <ArrowRight size={16} aria-hidden="true" />
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : searching &&
              (suggestionsLoading || suggestionsEmpty || suggestionsQuery.isError) ? (
              <div className="delivery-suggestion-popover delivery-suggestion-status" role="status">
                {suggestionsLoading ? (
                  <>
                    <SpinnerIcon size={20} className="delivery-spinner" aria-hidden="true" />
                    <span>Ищем адрес…</span>
                  </>
                ) : (
                  <>
                    <MapPinIcon size={21} aria-hidden="true" />
                    <div>
                      <strong>
                        {suggestionsQuery.isError ? 'Подсказки недоступны' : 'Не нашли этот адрес'}
                      </strong>
                      <p>
                        {suggestionsQuery.isError
                          ? 'Введите улицу и дом, затем нажмите «Проверить доставку».'
                          : 'Уточните улицу и номер дома или проверьте введённый адрес.'}
                      </p>
                    </div>
                  </>
                )}
              </div>
            ) : null}
          </div>
          <p className="delivery-address-hint" id={`${id}-address-hint`}>
            Например, Большая Садовая, 55
          </p>
        </div>
        <button
          className="delivery-primary-button"
          type="submit"
          disabled={pending || !city || !query.trim()}
        >
          {pending ? (
            <>
              Проверяем адрес <SpinnerIcon size={21} className="delivery-spinner" />
            </>
          ) : (
            <>
              Проверить доставку <ArrowRight size={21} />
            </>
          )}
        </button>
        {citiesError && (
          <div className="delivery-form-error" role="alert">
            Не получилось загрузить города.{' '}
            <button type="button" onClick={onRetryCities}>
              Повторить
            </button>
          </div>
        )}
        {error && (
          <p className="delivery-form-error" id={`${id}-error`} role="alert">
            {error}
          </p>
        )}
      </form>

      <div className="delivery-address-feedback" aria-live="polite" aria-atomic="true">
        {result ? (
          <div className={`delivery-result${result.isInZone ? ' is-available' : ''}`}>
            <div className="delivery-result-title">
              {result.isInZone ? <CheckIcon size={20} weight="bold" /> : <MapPinIcon size={20} />}
              <h3>{result.isInZone ? 'Да, доставляем!' : 'Адрес вне зоны доставки'}</h3>
            </div>
            <p className="delivery-result-address">{checkedAddress}</p>
            {result.isInZone ? (
              <>
                <dl className="delivery-result-terms">
                  <div>
                    <dt>Доставка</dt>
                    <dd>
                      {result.deliveryCost != null && Number(result.deliveryCost) === 0
                        ? 'Бесплатно'
                        : deliveryMoney(result.deliveryCost)}
                    </dd>
                  </div>
                  <div>
                    <dt>Заказ от</dt>
                    <dd>{deliveryMoney(result.minOrderSum)}</dd>
                  </div>
                  <div>
                    <dt>Ориентир по времени</dt>
                    <dd>{deliveryTimeLabel(result.estimatedTime)}</dd>
                  </div>
                </dl>
                {Number(result.deliveryCost) > 0 &&
                  Number(result.deliveryCost) <= freeDelivery.max_zone_cost &&
                  freeDelivery.min_sum > 0 && (
                    <p className="delivery-free-note">
                      Бесплатная доставка при заказе от {deliveryMoney(freeDelivery.min_sum)}.
                    </p>
                  )}
                <Link href="/#menu" className="delivery-result-link">
                  Выбрать роллы <ArrowRight size={18} />
                </Link>
              </>
            ) : (
              <p className="delivery-outside-note">
                {result.message ||
                  'Пока не привозим по этому адресу. Позвоните нам — уточним возможные варианты доставки.'}
              </p>
            )}
          </div>
        ) : (
          !pending &&
          !error && (
            <div className="delivery-before-check">
              <p>
                Стоимость зависит от зоны.
                <br />
                Проверка адреса займёт пару секунд.
              </p>
              {hydrated && savedAddress?.address && !query && (
                <button
                  type="button"
                  onClick={() => {
                    setQuery(savedAddress.address);
                    void check(savedAddress.address, savedAddress.coordinates);
                  }}
                >
                  Проверить сохранённый адрес <ArrowRight size={15} />
                </button>
              )}
            </div>
          )
        )}
      </div>
      <a className="delivery-panel-help" href="tel:+78633335050">
        Не нашли свой адрес? <span>Позвоните нам ↗</span>
      </a>
    </div>
  );
}
